import type { MetricKey } from '@muzly/voice-engine';

import { brightnessPosition, stepOfPosition } from '../onboarding/resultCopy';

import type { CopyRef } from '../../core';
import type { DiagnosisRecord } from '../onboarding/record';
import type { MetricTabId } from '../onboarding/resultCopy';

/**
 * homeTiles — 마지막 회차 → 홈의 지표 타일 일곱(시안 §7.1). 순수 변환이다.
 *
 * 타일은 결과 상세의 **탭과 같은 일곱**이고(`METRIC_TAB_IDS`가 그 목록의 주인이다),
 * 누르면 그 탭으로 간다(`/result/:id/:tab`).
 *
 * **순서는 상세와 다르다.** 시안 §7.1은 `음정`을 `음역`보다 앞에 두고 상세 §8.2는 반대다.
 * 목록이 같고 순서만 다른 것이라 여기 순서를 상세에 맞추면 홈이 시안과 어긋난다.
 *
 * **값이 없으면 `null`이다**(0도 `00`도 아니다). 시안은 아직 못 재는 자리를 `00`으로
 * 그려 뒀는데, 그건 자리표시자지 값이 아니다 — 그대로 옮기면 화면이 0점을 말한다.
 *
 * React·저장소 IO 의존 없음 — vitest 대상.
 */

export interface HomeTile {
  /** 누르면 갈 상세 탭. 타일 목록과 탭 목록이 같은 이름을 쓴다. */
  tab: MetricTabId;
  /**
   * 화면에 낼 값. 못 잰 지표는 null이고 화면은 `측정 준비 중`을 그린다.
   *
   * **서식이 붙은 값은 여기 없다** — 단위와 구분자는 로케일이 갖는다(`valueKey`).
   * 코드가 `${low}-${high}`를 만들면 en 로케일이 그 자리를 못 건드린다.
   */
  value: string | null;
  /**
   * 서식이나 문장이 필요한 값의 i18n 키. 렌더에서 번역한다 — 모듈 초기화 때 번역해 두면
   * 언어를 바꿔도 이 줄만 안 바뀐다.
   */
  valueKey?: string;
  /** `valueKey`에 넣을 값들. */
  valueParams?: Record<string, string | number>;
}

/**
 * 밝기 타일의 i18n 키 — **상세 화면과 같은 문장**을 쓴다. 같은 값을 홈이 `밝은 편`,
 * 상세가 다른 말로 부르면 두 지표로 읽힌다.
 */
export const brightnessLabelKey = (brightnessHz: number): string =>
  `web.result.resonance.brightnessLevel.${stepOfPosition(
    brightnessPosition(brightnessHz),
  )}`;

/** @param record 마지막 회차. 없으면 호출부가 타일을 안 그린다. */
export const homeTiles = (record: DiagnosisRecord): HomeTile[] => {
  // 키를 `MetricKey`로 좁힌다 — `string`이면 오타가 컴파일을 통과해 그 타일이 조용히
  // `측정 준비 중`으로 뜬다. 값이 없다는 판정을 오타가 대신하게 두지 않는다.
  const scoreOf = (key: MetricKey): string | null => {
    const found = record.scores.find((entry) => entry.key === key);
    return found ? String(Math.round(found.val)) : null;
  };

  return [
    // 피로도는 축을 내는 산식이 엔진에 없다 — 상세 탭도 같은 이유로 비어 있다.
    { tab: 'fatigue', value: null },
    { tab: 'registers', value: scoreOf('pitchAccuracy') },
    {
      // 서식은 로케일이 갖는다. 상세(`web.result.range.notes`)는 `E2 ~ C5`로 넓게 쓰고
      // 홈 타일은 좁아서 `E2-C5`로 붙인다 — 시안 §7.1과 §8.4가 실제로 그렇게 다르다.
      tab: 'range',
      value: null,
      valueKey: record.range ? 'web.home.tiles.range' : undefined,
      valueParams: record.range
        ? { low: record.range.lowNote, high: record.range.highNote }
        : undefined,
    },
    // 떨림 타일은 **속도**를 낸다(시안 §7.1). 탭 머리의 `vibrato` 점수는 속도·폭을 참고
    // 구간에 댄 판정이라 타일 칸에 넣으면 단위 없는 숫자가 된다.
    {
      tab: 'vibrato',
      value: null,
      valueKey: record.vibrato ? 'web.home.tiles.hz' : undefined,
      valueParams: record.vibrato
        ? { value: record.vibrato.rateHz.toFixed(1) }
        : undefined,
    },
    { tab: 'breath', value: scoreOf('sustain') },
    // 싱어즈 포먼트는 점수가 아니라 원값(중심 주파수)이 저장된다.
    {
      tab: 'formant',
      value: null,
      valueKey:
        record.resonanceCenterHz == null ? undefined : 'web.home.tiles.hz',
      valueParams:
        record.resonanceCenterHz == null
          ? undefined
          : { value: Math.round(record.resonanceCenterHz) },
    },
    {
      tab: 'resonance',
      value: null,
      valueKey:
        record.brightnessHz == null
          ? undefined
          : brightnessLabelKey(record.brightnessHz),
    },
  ];
};

const two = (n: number): string => String(n).padStart(2, '0');

/**
 * 타일 헤더의 회차 날짜(시안 `2026. 00. 00`) — 로컬 날짜 필드를 로케일 서식에 끼운다
 * (`history/historyRows.ts`의 `formatDate`와 같은 방식, 연도까지 있는 변형).
 */
export const tileDate = (epochMs: number): CopyRef => {
  const d = new Date(epochMs);
  return {
    key: 'web.home.tiles.date',
    params: {
      year: d.getFullYear(),
      month: two(d.getMonth() + 1),
      day: two(d.getDate()),
    },
  };
};
